import { field } from '../fields'
import Modal from './Modal'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { createClass, fetchNewClassOptions } from '../api/classAdmin'
import { addDays, formatFullDate, today } from '../api/calendar'
import type { ClassTypeView } from '../api/classTypes'

/**
 * The coming Saturday, as the makeup date's starting point (D-409).
 *
 * <b>A default, never a rule.</b> The owner: <i>"saturday/sunday is just when
 * they normally do them, we dont have to restrict when though."</i> So this
 * saves her a calendar lookup for the usual case and gets out of the way for
 * every other one.
 *
 * UTC-anchored day maths, so it is DST-proof (D-68); `today()` reads the local
 * clock, which is what makes "the coming Saturday" mean what she would mean.
 */
function nextSaturday(): string {
  const from = today()
  const day = new Date(`${from}T00:00:00Z`).getUTCDay()

  // Saturday is 6. On a Saturday this gives the *next* one rather than today,
  // which is right: a session created today for today is not what she is doing.
  return addDays(from, ((6 - day + 7) % 7) || 7)
}
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Adding a class the studio runs (D-207).
 *
 * <b>Until now every class had to come from Jackrabbit.</b> A workshop, a
 * summer intensive or a section added mid-season could not be recorded at all,
 * which is the gap the owner named: *"we also have no way of adding a class
 * right now"*.
 *
 * Two hazards shape the form, and both are real:
 *
 * <b>Dates.</b> A class with no occurrences appears on no calendar and in no
 * register with nothing saying why (D-89) — so the server generates them on
 * creation, and this says which dates it will run across rather than asking.
 *
 * <b>Duplicates, the worse one.</b> The import matches an app-made class on
 * name + weekday + start time (D-88, D-93). One character out and the next
 * import stands a second class beside it and splits the roster — silently. So
 * the form asks whether Jackrabbit holds it too, before anything else.
 */
export default function NewClassDialog({
  kinds,
  onCreated,
  onClose,
}: {
  kinds: ClassTypeView[]
  onCreated: () => void
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const { data: options } = useQuery({
    queryKey: ['new-class-options'],
    queryFn: fetchNewClassOptions,
  })

  const [alsoInJackrabbit, setAlsoInJackrabbit] = useState<boolean | null>(null)
  const [name, setName] = useState('')
  const [programId, setProgramId] = useState('')
  const [day, setDay] = useState('Monday')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [roomId, setRoomId] = useState('')
  const [kindId, setKindId] = useState('')

  /*
   * **Who teaches it** (D-404). Nothing in production assigned an instructor
   * before this, so every class the app made had none - invisible until a
   * level-up makeup needed one to approve it and the request reached nobody.
   */
  const [teacherId, setTeacherId] = useState('')

  /*
   * What a missed session of this class may be repaired by (scenario 9, D-295).
   *
   * All three start empty and stay empty unless she says otherwise. Defaulting
   * the category to Choreo or Tech would be the app deciding a studio policy
   * nobody stated — the same reason an import reports a missing Cat3 rather
   * than guessing at it (D-51, D-227).
   */
  const [makeupCategory, setMakeupCategory] = useState('')
  const [style, setStyle] = useState('')
  const [levelBand, setLevelBand] = useState('')

  /*
   * **A makeup session runs once, not all season** (D-402).
   *
   * Owner: *"technique makeups should be monthly, on a Saturday or Sunday"*
   * and *"makeups need a premade type so they aren't set up from scratch each
   * time."* Both are this: tick it, pick the day, and one session exists and
   * is already advertising what it repairs.
   *
   * Before this it was two screens — make the class here, then find its
   * occurrence on the makeup list and designate it there — and the class ran
   * every Saturday of the season, so she designated one and left thirty-nine
   * behind.
   */
  const [isMakeupSession, setIsMakeupSession] = useState(false)
  /*
   * **Defaults to the coming Saturday, and refuses nothing** (D-409).
   *
   * `asked`: *"they will make them when they need to, saturday/sunday is just
   * when they normally do them, we dont have to restrict when though."* So a
   * default rather than a guard — the studio's habit is worth saving her a
   * calendar lookup, and refusing a Wednesday would be the app deciding studio
   * policy, which is the line D-227 and D-51 both draw.
   *
   * Empty until the makeup box is ticked, because the field does not exist for
   * an ordinary class and a stray date on the request is what D-402 refused.
   */
  const [runsOn, setRunsOn] = useState('')
  const [minimum, setMinimum] = useState('')

  const create = useMutation({
    mutationFn: () =>
      createClass({
        name: name.trim(),
        studioProgramId: programId,
        dayOfWeek: day,
        startTime: startTime || null,
        endTime: endTime || null,
        roomId: roomId || null,
        classTypeId: kindId || null,
        teacherStaffId: teacherId || null,

        // Empty stays empty — trimmed to null rather than sent as "", which
        // would be the studio saying "no style" instead of "not said yet".
        makeupCategory: (makeupCategory || null) as 'Tech' | 'Choreo' | 'Recital' | null,
        style: style.trim() || null,
        levelBand: levelBand.trim() || null,

        alsoInJackrabbit: alsoInJackrabbit ?? false,

        /*
         * Only when it is a makeup, so an ordinary class is created exactly as
         * it always was — the server treats a missing `runsOn` as "runs the
         * season" (D-207), which is still right for everything else.
         */
        runsOn: isMakeupSession ? runsOn || null : null,
        isMakeupSession,
        makeupMinimumSignups: isMakeupSession && minimum ? Number(minimum) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
      onCreated()
    },
  })

  /*
   * **A makeup needs its day and its kind before Add means anything** (D-402).
   *
   * The server refuses both now, and it is the half that matters because this
   * dialog is not the only caller. This is the other half: without it the
   * button is enabled, she presses it, and the answer is an error message
   * rather than a field she can see is empty.
   *
   * Both were one click away and both returned 200 before a cold audit drove
   * the real form &mdash; no date ran the session every week of the season, and
   * no kind made a session that repairs nobody while the tick box promised it
   * "starts repairing absences straight away".
   */
  const makeupIsAnswered = !isMakeupSession || (runsOn.length > 0 && makeupCategory.length > 0)

  /*
   * <b>Every class says how it is made up, and a technique one says with
   * what</b> (D-507). `asked`: *"lets make sure we have validations that
   * require these fields upon creation since they are so crucial to the makeup
   * flow"* — because this app is becoming the source of truth, and a hole
   * created here has no import to blame and nobody looking for it.
   *
   * <b>Choreography needs neither, and that is not laxity.</b> A routine is
   * repaired by a private and never by a group session (D-405), so a style and
   * a level decide nothing for it — 124 of the studio's 186 classes are filed
   * that way and every one is right. A level typed to satisfy a form matches
   * the wrong dancers.
   *
   * The server enforces the same rule, from the same domain function, so the
   * form cannot be the only thing standing between us and a class the makeup
   * rule can never use.
   */
  const filedEnough =
    makeupCategory.length > 0
    && (makeupCategory !== 'Tech' || (style.trim().length > 0 && levelBand.trim().length > 0))

  const ready =
    name.trim().length > 0 && programId.length > 0 && makeupIsAnswered && filedEnough

  return (
    /*
      <b>On the shared shell</b> (D-515). No role, no aria-modal and no name,
      nothing closed it on Escape, and its backdrop was an absolutely-positioned
      button that sat in the tab order announcing itself as "Cancel".
    */
    <Modal
      onDismiss={onClose}
      label="Add a class"
      overlayClassName="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 px-5 py-8 backdrop-blur-[1px]"
      panelClassName="relative w-full max-w-[28rem] rounded-2xl border border-line bg-surface px-5 py-4 shadow-2xl"
    >
        <h2 className="font-display text-lg">Add a class</h2>

        {/*
          Asked first, and it is not a formality. Answering "yes" makes the name
          load-bearing: it has to match Jackrabbit's exactly or the next import
          creates a second class and splits the roster between them, with
          nothing on any screen saying so.
        */}
        {alsoInJackrabbit === null ? (
          <>
            <p className="mt-1 text-[13px] text-ink-3">
              Is this class also in Jackrabbit?
            </p>

            <div className="mt-4 flex flex-col gap-2">
              <Choice
                onClick={() => setAlsoInJackrabbit(false)}
                title="No, it is ours"
                detail="A workshop, a summer intensive, something Jackrabbit does not have. Imports will never touch it."
              />
              <Choice
                onClick={() => setAlsoInJackrabbit(true)}
                title="Yes, Jackrabbit has it too"
                detail="Then the name, day and time have to match Jackrabbit exactly, or the next import will add a second copy."
              />
            </div>

            <button
              type="button"
              onClick={onClose}
              className="mt-4 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {alsoInJackrabbit && (
              <p className="mt-2 flex items-start gap-2 rounded-lg bg-warn-bg px-3 py-2 text-[12px] text-ink-2">
                <AlertTriangle size={13} strokeWidth={2.5} className="mt-0.5 shrink-0 text-warn" />
                <span>
                  Copy the name, day and time from Jackrabbit exactly. A class that does not
                  match gets a second copy on the next import, with its dancers split between
                  the two.
                </span>
              </p>
            )}

            <div className="mt-3 flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  placeholder={
                    alsoInJackrabbit
                      ? 'TIPPY TOES | Tu 4:45 - Tap, Ballet, Tumbling'
                      : 'Summer Intensive'
                  }
                  className={field}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Programme</span>
                <select
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                  className={field}
                >
                  <option value="">Pick one</option>
                  {options?.programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="text-[11.5px] text-ink-3">
                  Decides whether the 80% attendance rule applies, and whether it can appear on
                  the website.
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                {/*
                  **Hidden once it is a makeup session** (D-402). A one-date
                  class takes its weekday from the date, so leaving this
                  editable is a control that lies (D-204) &mdash; it reads
                  Monday while the class comes out Saturday, and the value is
                  thrown away. It is also what made the duplicate guard compare
                  a weekday nothing uses.
                */}
                {!isMakeupSession && (
                  <label className="flex min-w-[8rem] flex-1 flex-col gap-1">
                    <span className="text-[12px] font-semibold text-ink-3">Day</span>
                    <select value={day} onChange={(e) => setDay(e.target.value)} className={field}>
                      {DAYS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="flex min-w-[10rem] flex-1 flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Teacher</span>
                  <select
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                    className={field}
                  >
                    <option value="">Not decided yet</option>
                    {(options?.teachers ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Starts</span>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className={`${field} w-[8rem]`}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Ends</span>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className={`${field} w-[8rem]`}
                  />
                </label>
              </div>

              {/*
                Said rather than left to be discovered. A class with no start
                time meets on no dates (D-84) — which is correct for the 86
                solos awaiting a slot, and a surprise to anyone else.
              */}
              {!startTime && (
                <p className="text-[11.5px] text-ink-3">
                  With no start time it will meet on no dates, and appear on no calendar. You
                  can add one later.
                </p>
              )}

              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Room</span>
                <select
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  className={field}
                >
                  <option value="">None yet</option>
                  {options?.rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Kind of class</span>
                <select
                  value={kindId}
                  onChange={(e) => setKindId(e.target.value)}
                  className={field}
                >
                  <option value="">Not in a kind</option>
                  {kinds.map((k) => (
                    <option key={k.classTypeId} value={k.classTypeId}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </label>

              {/*
                What can make a missed session of this class up (scenario 9,
                D-295).

                Offered here as well as on the panel so a new class is never
                briefly wrong: between creation and the first edit it otherwise
                has no category at all, and a session of it in that window is
                offered to nobody.

                <b>And now every one of them must be answered</b> (D-507,
                amending D-295). Leaving them alone was defensible while an
                unfiled class merely waited to be filed; D-405 made it send a
                family to a paid private instead of a free makeup, and this app
                is becoming the source of truth, where a hole made here has no
                import to blame and nobody looking for it.

                <b>Choreography still needs neither a style nor a level</b>, and
                that is not laxity: a routine is repaired by a private, never a
                group session, so those two fields decide nothing for it. 124 of
                the studio's 186 classes are filed exactly that way and every one
                is right.
              */}
              <div className="flex flex-wrap gap-2">
                <label className="flex min-w-[9rem] flex-1 flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Makeups</span>
                  <select
                    value={makeupCategory}
                    onChange={(e) => setMakeupCategory(e.target.value)}
                    className={field}
                  >
                    <option value="">Not said yet</option>
                    <option value="Tech">Technique</option>
                    {/*
                      **Kept for a class, refused for a makeup session**
                      (D-405). This one field does both jobs: it files the class
                      — which is exactly how a routine reaches the private path
                      — and, when the tick below is on, it also says what the
                      session repairs.

                      A routine is repaired by a private, so a session
                      designated for choreo is offered to nobody and expires
                      silently. Removing the option outright would take away the
                      only way to file a comp routine at all, so it is disabled
                      only in the case that breaks, and it says why.
                    */}
                    <option value="Choreo" disabled={isMakeupSession}>
                      {isMakeupSession
                        ? 'Choreography — made up in a private, not a session'
                        : 'Choreography'}
                    </option>
                    <option value="Recital">Recital</option>
                  </select>
                </label>

                <label className="flex min-w-[7rem] flex-1 flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Style</span>
                  <input
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                    placeholder="Ballet"
                    className={field}
                  />
                </label>

                <label className="flex min-w-[6rem] flex-1 flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Level</span>
                  <input
                    value={levelBand}
                    onChange={(e) => setLevelBand(e.target.value)}
                    placeholder="3"
                    className={field}
                  />
                </label>
              </div>

              <p className="text-[11.5px] text-ink-3">
                {/* Combined levels are how the studio actually writes them (D-274). */}
                Levels can be combined — write 2/3 for a class that covers both. Leave
                any of these empty if you would rather decide later.
              </p>

              {/*
                **The one-session switch** (D-402). Placed under the makeup
                fields because it is only meaningful once she has said what the
                session repairs — a date with no category advertises nothing.
              */}
              <label className="flex items-start gap-2.5 text-[13px]">
                <input
                  type="checkbox"
                  checked={isMakeupSession}
                  onChange={(e) => {
                    setIsMakeupSession(e.target.checked)
                    /*
                      Ticking this refuses choreo (D-405), and a value already
                      chosen would otherwise sit there selected-but-disabled —
                      submitting to a 400 she cannot see the cause of, because
                      the select still reads Choreography. Clearing it puts her
                      back at "Not said yet", which the form already refuses
                      with a sentence.
                    */
                    if (e.target.checked && makeupCategory === 'Choreo') setMakeupCategory('')

                    /*
                      The coming Saturday, so she is not looking up a date the
                      studio almost always uses anyway (D-409). Only when she
                      has not already picked one, and only on the way in — it is
                      a starting point, not a correction.
                    */
                    if (e.target.checked && runsOn === '') setRunsOn(nextSaturday())
                  }}
                  className="mt-0.5 size-4 shrink-0"
                />
                <span className="min-w-0">
                  This is a makeup session
                  <span className="mt-0.5 block text-[11.5px] text-ink-3">
                    It runs once, on the day you pick, and starts repairing absences straight away.
                  </span>
                </span>
              </label>

              {/*
                ⚠ <b>A select used to sit here</b> labelled *"Start from one you
                have made"*, offering the six most recent designated sessions and
                applying ten setters on pick (D-410). It answered the owner's
                *"makeups need a premade type so they aren't set up from scratch
                each time"* — the *each time* D-402 left over.

                <b>It answered it from the wrong end</b>: you began a new class and
                then said which old one to copy. `asked`, 2026-09-27, she called
                that janky and asked for the opposite — open the session you
                already made and give it another date.

                <b>So the control lives on the class record now</b> (D-837) and this
                dialog is for a genuinely new class again. Two doors to one idea
                drift (A-17), and this was the one nobody could find.
              */}
              {isMakeupSession && (
                <div className="flex flex-wrap items-end gap-3">
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-semibold text-ink-2">
                      The day it runs
                    </span>
                    <input
                      type="date"
                      value={runsOn}
                      onChange={(e) => setRunsOn(e.target.value)}
                      className={field}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-[12px] font-semibold text-ink-2">
                      Dancers needed
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={minimum}
                      onChange={(e) => setMinimum(e.target.value)}
                      placeholder="3"
                      className={`${field} tabular w-28`}
                    />
                  </label>
                </div>
              )}

              {/*
                **Says which of the two it is about to make.** The sentence used
                to be unconditional, so a one-session makeup was announced as
                running all season — the exact thing D-402 exists to stop, said
                out loud to the person doing it.
              */}
              {isMakeupSession ? (
                <p className="text-[11.5px] text-ink-3">
                  {runsOn
                    ? `One session, on ${formatFullDate(runsOn)}. Leave the number empty to use the studio default.`
                    : 'Pick the day it runs. Leave the number empty to use the studio default.'}
                </p>
              ) : (
                options?.seasonName && (
                  <p className="text-[11.5px] text-ink-3">
                    It will run across {options.seasonName}, every {day}.
                  </p>
                )
              )}

              {/*
                Hidden until she says otherwise (owner-confirmed): build it,
                check it, publish it. The alternative puts a half-finished class
                in front of families for as long as it takes to notice.
              */}
              <p className="text-[11.5px] text-ink-3">
                It starts off the website. Open it and tick &ldquo;Show on the website&rdquo;
                when it is ready.
              </p>

              {create.isError && (
                <p role="alert" className="text-[12.5px] text-crit">
                  {(create.error as Error).message}
                </p>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={!ready || create.isPending}
                  onClick={() => create.mutate()}
                  className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
                >
                  {create.isPending ? 'Adding...' : 'Add the class'}
                </button>

                <button
                  type="button"
                  onClick={() => setAlsoInJackrabbit(null)}
                  className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
                >
                  Back
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="ml-auto text-[12.5px] font-semibold text-ink-3 hover:text-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
    </Modal>
  )
}

function Choice({
  onClick,
  title,
  detail,
}: {
  onClick: () => void
  title: string
  detail: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-line-2 px-3.5 py-3 text-left hover:border-accent"
    >
      <span className="block text-[13.5px] font-semibold">{title}</span>
      <span className="mt-0.5 block text-[12px] text-ink-3">{detail}</span>
    </button>
  )
}
